import { useQueries } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { deflateSync } from 'fflate';
import { getPrincipalQuery } from 'src/selectors/getPrincipalQuery';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { TemplateDiscoveryService } from 'src/services/TemplateDiscoveryService';
import { DocumentSourceQuery } from 'src/hooks/useDocumentSource';

type CimpressDocument = Record<string, unknown>;

interface MatchingVisualizerLoadParams {
    documentSourceUri: string;
    productKey: string;
    productVersion: number;
    culture: string;
    fullProductSpecification: Record<string, string>;
    pdsResultUri?: string;
    dpcId?: string;
}

interface MatchingVisualizerResults {
    pdsResultPreview: {
        isLoading: boolean;
        error: Error | null;
        previewUrl: string | null;
    };
    templateDiscoveryResult: {
        isLoading: boolean;
        error: Error | null;
        designConceptId: string | null;
    };
    documentSourcePreviewUrl: string | null;
    isLoading: boolean;
    hasError: boolean;
    errors: (Error | null)[];
    warnings: string[];
}

const encodeCimDoc = ({ cimDoc }: { cimDoc: CimpressDocument }): string => {
    const documentJson = JSON.stringify(cimDoc);
    const encoder = new TextEncoder();

    const encodedValue = encoder.encode(documentJson);
    const valueDeflated = deflateSync(encodedValue);

    if (typeof btoa === 'undefined') {
        return Buffer.from(valueDeflated).toString('base64');
    }

    return btoa(String.fromCharCode(...valueDeflated));
};

export const generatePreviewURI = async (document: CimpressDocument, height = '400'): Promise<string> => {
    const encodedDocumentStr = encodeCimDoc({ cimDoc: document });

    const instructionsURI = `https://instructions.documents.cimpress.io/v3/instructions:preview`;
    const renderingURL = `https://rendering.documents.cimpress.io/v2/example/preview?height=${height}&instructions_uri=${encodeURIComponent(instructionsURI)}`;

    const formData = new FormData();
    const instructionsPostData = JSON.stringify({ documentUri: encodedDocumentStr });

    formData.append('instructionsPostData', instructionsPostData);

    const response = await fetch(renderingURL, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`Failed to generate preview: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    return URL.createObjectURL(blob);
};

export const useMatchingVisualizerLoad = (
    params: MatchingVisualizerLoadParams | null,
    documentSourceQuery: DocumentSourceQuery,
): MatchingVisualizerResults => {
    const canonicalId = useRecoilValue(getPrincipalQuery);
    const accessToken = useRecoilValue(isAuthorizedQuery);

    const hasDocumentSource = !!documentSourceQuery.data && !documentSourceQuery.error;
    const enabled = !!params && !!canonicalId && !!accessToken && hasDocumentSource;

    const pdsResultQuery = useQueries({
        queries: [
            {
                queryKey: ['siteResult', params?.pdsResultUri],
                queryFn: async (): Promise<string> => {
                    if (!params?.pdsResultUri) throw new Error('Missing Site Result URI');

                    const response = await fetch(params.pdsResultUri);
                    const data = await response.json() as CimpressDocument;

                    return generatePreviewURI(data);
                },
                enabled: !!params && !!accessToken && !!params?.pdsResultUri,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
        ],
    })[0];

    const templateIdentifier = documentSourceQuery.data?.templateIdentifier;

    const isStaticDesign = templateIdentifier?.type === 'TEMPLATE_TOKEN';
    const templateToken = isStaticDesign
        ? templateIdentifier.value
        : undefined;

    const templateDiscoveryQuery = useQueries({
        queries: [
            {
                queryKey: ['templateDiscovery', templateToken, accessToken],
                queryFn: async (): Promise<string | null> => {
                    if (!templateToken || !accessToken) {
                        throw new Error('Missing template token or access token');
                    }

                    const ensembleLines = await TemplateDiscoveryService.getEnsembleLinesFromTemplateToken(
                        templateToken,
                        accessToken,
                    );

                    return ensembleLines[0]?.ensembles[0]?.designProperties?.isProminentColorVariant !== undefined
                        ? ensembleLines[0].designConceptId
                        : null;
                },
                enabled: enabled && isStaticDesign && !!templateToken,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
        ],
    })[0];

    const documentSourcePreviewQuery = useQueries({
        queries: [
            {
                queryKey: ['documentSourcePreview', documentSourceQuery.data],
                queryFn: async (): Promise<string> => {
                    const cimDoc = documentSourceQuery.data as CimpressDocument;

                    return generatePreviewURI(cimDoc);
                },
                enabled: !!params && !!canonicalId && !!accessToken && hasDocumentSource,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
        ],
    })[0];

    const documentSourcePreviewUrl = documentSourcePreviewQuery.data ?? null;

    return {
        pdsResultPreview: {
            isLoading: params?.pdsResultUri ? pdsResultQuery.isInitialLoading : false,
            error: pdsResultQuery.error as Error | null,
            previewUrl: pdsResultQuery.data ?? null,
        },
        templateDiscoveryResult: {
            isLoading: params && isStaticDesign ? templateDiscoveryQuery.isInitialLoading : false,
            error: templateDiscoveryQuery.error as Error | null,
            designConceptId: templateDiscoveryQuery.data ?? null,
        },
        documentSourcePreviewUrl,
        isLoading: false,
        hasError: false,
        errors: [],
        warnings: [],
    };
};
